import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/enhanced-button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { forgotPassword } from "@/services/auth.service";

const schema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type FormValues = z.infer<typeof schema>;

const ForgotPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    try {
      await forgotPassword(values.email);
    } catch {
      // Ignore errors — always show success to prevent info leak
    } finally {
      setIsLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-xl bg-card/95 backdrop-blur-sm">
          <CardContent className="p-8">
            <div className="mb-8 text-center">
              <div className="w-20 h-20 mx-auto mb-4">
                <img src="/content/logo.png" alt="BlinkBooks Logo" className="w-full h-full object-contain" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Reset your password</h1>
              {!submitted && (
                <p className="text-muted-foreground">
                  Enter your email and we'll send you a reset link.
                </p>
              )}
            </div>

            {submitted ? (
              <div className="text-center">
                <p className="text-foreground mb-6">
                  If that email exists, we sent a reset link. Check your inbox.
                </p>
                <button
                  onClick={() => navigate("/")}
                  className="text-sm text-muted-foreground underline hover:text-foreground transition-colors"
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="you@company.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send reset link"
                    )}
                  </Button>
                </form>
              </Form>
            )}

            {!submitted && (
              <p className="mt-6 text-center text-sm text-muted-foreground">
                Remembered your password?{" "}
                <button
                  onClick={() => navigate("/")}
                  className="underline hover:text-foreground transition-colors font-medium"
                >
                  Sign in
                </button>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
